import { useCallback, useEffect, useState, memo } from "react";
import { AuthView } from "../AuthView/AuthView";
import { Trans } from "react-i18next";

import "./UserSignIn.css";
import { useMatch, useSearchParams } from "react-router";
import { useAppDispatch, useAppSelector } from "@store/store";
import {
  setAuthViewAction,
  setResetPasswordTokenAction,
  resetAuthViewAction,
  setIsAuthViewOpenAction,
  refreshAuthEmailAction,
  selectIsEmailRefreshed,
} from "@store/sessionReducer";
import { useNavigate } from "@hooks/useNavigate/useNavigate";
import { logError } from "@lib/logger";
import { signInWithMagicLinkAction } from "@store/auth/actions/signInWithMagicLinkAction";

/**
 * Sign in link button, will trigger sign in/up modal when pressed.
 */
const UserSignIn = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmailRefreshed = useAppSelector(selectIsEmailRefreshed);

  // Password reset flow
  const isPasswordReset = useMatch("/users/password/edit");
  const resetPasswordToken = searchParams.get("reset_password_token");
  useEffect(() => {
    if (isPasswordReset && isEmailRefreshed) {
      if (resetPasswordToken) {
        dispatch(setResetPasswordTokenAction({ resetPasswordToken }));
        dispatch(setAuthViewAction({ authView: "resetPassword" }));
        dispatch(setIsAuthViewOpenAction({ isAuthViewOpen: true }));
      } else {
        logError("No reset password token found");
        dispatch(setAuthViewAction({ authView: "resetPasswordExpired" }));
        dispatch(setIsAuthViewOpenAction({ isAuthViewOpen: true }));
      }
      navigate("/", {
        searchParamsToRemove: ["reset_password_token", "locale"],
        replace: true,
      });
    }
  }, [
    isPasswordReset,
    dispatch,
    resetPasswordToken,
    navigate,
    isEmailRefreshed,
  ]);

  // Magic link flow
  const [isMagicLinkTokenSent, setIsMagicLinkTokenSent] = useState(false);
  const isMagicLink = useMatch("/users/magic_link_token");
  const magicLinkToken = searchParams.get("token");

  useEffect(() => {
    dispatch(refreshAuthEmailAction());
  }, [dispatch]);

  useEffect(() => {
    const handleMagicLink = async () => {
      if (isMagicLink && !isMagicLinkTokenSent && isEmailRefreshed) {
        if (magicLinkToken) {
          // Prevent multiple sign in attempts being sent
          setIsMagicLinkTokenSent(true);
          try {
            await dispatch(signInWithMagicLinkAction(magicLinkToken));
          } catch (_error) {
            dispatch(setAuthViewAction({ authView: "magicLinkExpired" }));
            dispatch(setIsAuthViewOpenAction({ isAuthViewOpen: true }));
          }
        } else {
          logError("No magic link token found");
        }
        navigate("/", {
          searchParamsToRemove: ["token", "locale"],
          replace: true,
        });
      }
    };
    handleMagicLink();
  }, [
    isMagicLink,
    dispatch,
    magicLinkToken,
    navigate,
    isMagicLinkTokenSent,
    isEmailRefreshed,
  ]);

  const handleSignIn = useCallback(() => {
    dispatch(setIsAuthViewOpenAction({ isAuthViewOpen: true }));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(setIsAuthViewOpenAction({ isAuthViewOpen: false }));
    dispatch(resetAuthViewAction());
  }, [dispatch]);

  return (
    <div className="user-sign-in-component">
      <AuthView handleClose={handleClose} />

      <a className="sign-in-link" onClick={handleSignIn}>
        <Trans i18nKey="authentication.signIn" />
      </a>
    </div>
  );
});

UserSignIn.displayName = "UserSignIn";

export { UserSignIn };
